import { getFirst, isValid } from 'clients/Clients';
import { useCart } from 'context/Cart/CartContext';
import { RefObject, useEffect, useState } from 'react';
import { ProductCart } from 'types/product';
import { debounceFunc400 } from 'utils/debounce';

const UPDATE = 'update';
const REMOVE = 'remove';
const REGEX_NUMBER = new RegExp('^[0-9]+$');

type UpdateType = 'update' | 'remove';

export default function useCartQuickOrder(product: ProductCart, inputRef: RefObject<HTMLInputElement>) {
  const { quantityInCart, maxQuantity } = product;
  const [currentValue, setCurrentValue] = useState(0);
  const [valueChange, setValueChange] = useState(0);
  const [isShowModalRemove, setIsShowModalRemove] = useState(false);
  const { removeCartItemQuickOrder, updateCartItemQuickOrder } = useCart();
  const productQuantity = quantityInCart || 0;

  const updateCart = async (quantity: string, isReload = true) => {
    const response = await updateCartItemQuickOrder(+quantity, product, isReload);

    if (isValid(response) && inputRef.current) {
      const quantityResponse = response.quantityResponse || null;
    } else {
      setCurrentValue(0);
    }
  };

  const handleCart = (val: string, updateType: UpdateType) => {
    switch (updateType) {
      case UPDATE:
        updateCart(val);
        break;
      case REMOVE:
        removeCartItemQuickOrder(product, true);
        setValueChange(0);
        break;
      default:
        break;
    }
  };

  const handler = (val: string, updateType: UpdateType) => {
    debounceFunc400(() => {
      handleCart(val, updateType);
    });
  };

  const confirmRemove = () => {
    if (inputRef.current) inputRef.current.value = '0';
    setValueChange(0);
    setIsShowModalRemove(false);
    return handler(0, REMOVE);
  };

  const closeModal = () => {
    if (inputRef.current) {
      setValueChange(1);
      inputRef.current.value = String(1);
      handler(String(1), UPDATE);
    }
    setIsShowModalRemove(false);
  };

  const handleDecrease = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (inputRef.current) {
      const newValue = +inputRef.current.value - 1;

      if (newValue >= 0) {
        if (newValue === 0) {
          return setIsShowModalRemove(true);
        }
        inputRef.current.value = String(newValue);
        setValueChange(newValue);
        return handler(newValue, UPDATE);
      }
      return null;
    }
    return null;
  };

  const handleIncrease = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (inputRef.current) {
      const newValue = +inputRef.current.value + 1;

      if (maxQuantity > 0 && newValue > maxQuantity) {
        return null;
      }

      setValueChange(newValue);
      inputRef.current.value = String(newValue);

      return handler(newValue, UPDATE);
    }

    return null;
  };

  const handleBlurInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (+value !== +currentValue) {
      if (+value && REGEX_NUMBER.test(value)) {
        return handler(value, UPDATE);
      }
    }
    if (+value === 0 && +value !== valueChange) {
      return setIsShowModalRemove(true);
    }
    return null;
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!inputRef.current) return null;
    if (value === '00') {
      inputRef.current.value = '0';
    } else if (value && value !== '0') {
      let newValue = parseInt(value, 10);

      // Check if newValue exceeds maxQuantity or 10000
      if ((maxQuantity > 0 && newValue > maxQuantity) || newValue > 10000) {
        newValue = maxQuantity > 0 ? maxQuantity : 10000;
      }

      // Validate input value using REGEX_NUMBER
      if (!REGEX_NUMBER.test(String(newValue))) {
        newValue = 0;
      }

      // Update the input value and state
      inputRef.current.value = String(newValue);
      setValueChange(newValue);
    } else {
      inputRef.current.value = '0';
    }
  };

  const handleFocus = () => {
    setCurrentValue(valueChange);
  };

  useEffect(() => {
    setCurrentValue(productQuantity);
    setValueChange(productQuantity);
    if (inputRef.current) inputRef.current.value = String(productQuantity);
  }, []);

  return [handleIncrease, handleBlurInput, handleChangeInput, handleFocus, handleDecrease, confirmRemove, closeModal, isShowModalRemove, valueChange];
}
